<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { getLanguageFlagUrl } from '@/utils/locales/messages';
import { getTimezoneFromJurisdiction } from '@/shared/composables/dateUtils';
import { useAccountSettingsStore } from '@/features/account-settings/stores/accountSettingsStore';

const { t } = useI18n();
const languageStore = useLanguageStore();
const sessionStore = useSessionStore();
const accountSettingsStore = useAccountSettingsStore();

// --- Language ---
// 헤더에는 "지금 실제로 적용된 UI 언어"(languageStore, localStorage 기반)를 표시한다.
// DB의 계정 기본 언어(sessionStore.user.user.language)는 새 기기/최초 로그인 시의 초기값일 뿐이며,
// changeLanguage()에서 DB 업데이트가 실패해도 실제 적용 언어와는 무관하므로 폴백으로만 사용한다.
const defaultLangCd = computed(() => {
  return (languageStore.currentLanguage.value || sessionStore.user?.user?.language || 'EN').toLowerCase();
});

const supportedLanguages = computed(() => {
  return languageStore.supportedLanguages.value.map(lang => ({
    title: lang.nativeName,
    subtext: lang.name,
    value: lang.code,
    avatar: getLanguageFlagUrl(lang.code, 20)
  }));
});

// supportedLanguages에서 매칭하여 국기 URL 가져오기 (LanguageDD와 동일 방식)
const currentFlag = computed(() => {
  const dbLang = defaultLangCd.value;
  const matched = supportedLanguages.value.find(
    l => l.value === dbLang || l.value.toUpperCase() === dbLang.toUpperCase()
  );
  return matched?.avatar || '';
});

const langSyncFailedSnackbar = ref(false);

// 상단 언어/날짜 변경은 같은 계정 설정 PATCH + 세션 재초기화를 부른다.
// 연속·동시 클릭 시 요청이 순서 뒤바뀌어 저장·표시가 어긋나지 않도록,
// 하나의 busy 플래그로 변경을 직렬화하고 진행 중에는 셀렉터를 잠근다.
// busyKey는 스피너를 어느 셀렉터에 보여줄지 구분한다.
const busy = ref(false);
const busyKey = ref<'lang' | 'date' | null>(null);

const changeLanguage = async (langCode: string) => {
  if (busy.value) return;                                         // 진행 중이면 무시(연속 클릭 방지)
  if (langCode === languageStore.currentLanguage.value) return;   // 같은 언어면 불필요한 저장 생략
  busy.value = true;
  busyKey.value = 'lang';
  // 실패 시 되돌아갈 언어 — 지금 화면에 이미 확인된(직전) 언어. 영어 강제 고정이 아니다.
  const previousLangCode = languageStore.currentLanguage.value;
  // UI 언어 변경 (localStorage) — DB 저장 확인 전까지는 낙관적 적용
  languageStore.setLanguage(langCode);
  // DB Default Language 업데이트
  try {
    const userInfo = sessionStore.getUserInfo();
    await accountSettingsStore.updateAccount({
      firstName: userInfo?.firstName || '',
      lastName: userInfo?.lastName || '',
      defaultLangCd: langCode.toUpperCase(),
    });
  } catch (error) {
    console.error('Failed to update default language:', error);
    // DB 저장 실패 시 화면 언어가 계정 설정과 영구히 어긋난 채 남지 않도록 되돌리되,
    // 무조건 영어가 아니라 시도 전에 실제로 쓰고 있던 언어로 되돌린다(예: 중국어 사용 중
    // 구독 만료로 저장이 막히면 중국어에 그대로 머물러야지 영어로 튈 이유가 없다).
    languageStore.setLanguage(previousLangCode);
    langSyncFailedSnackbar.value = true;
  } finally {
    busy.value = false;
    busyKey.value = null;
  }
};

// --- Date Format (reactive) ---
const dateFormat = computed(() => {
  return sessionStore.user?.user?.dateFormat || 'YYYY-MM-DD';
});

const dateFormatOptions = [
  { value: 'YYYY-MM-DD', title: 'YYYY-MM-DD (2024-01-15)' },
  { value: 'MM/DD/YYYY', title: 'MM/DD/YYYY (01/15/2024)' },
  { value: 'DD/MM/YYYY', title: 'DD/MM/YYYY (15/01/2024)' },
  { value: 'YYYY/MM/DD', title: 'YYYY/MM/DD (2024/01/15)' },
  { value: 'DD-MM-YYYY', title: 'DD-MM-YYYY (15-01-2024)' },
  { value: 'YYYY.MM.DD', title: 'YYYY.MM.DD (2024.01.15)' },
  { value: 'MM.DD.YYYY', title: 'MM.DD.YYYY (01.15.2024)' },
  { value: 'DD.MM.YYYY', title: 'DD.MM.YYYY (15.01.2024)' }
];

const changeDateFormat = async (format: string) => {
  if (busy.value) return;                       // 진행 중이면 무시(연속 클릭 방지)
  if (format === dateFormat.value) return;      // 같은 포맷이면 불필요한 저장 생략
  busy.value = true;
  busyKey.value = 'date';
  try {
    const userInfo = sessionStore.getUserInfo();
    await accountSettingsStore.updateAccount({
      firstName: userInfo?.firstName || '',
      lastName: userInfo?.lastName || '',
      dateFormat: format,
    });
  } catch (error) {
    console.error('Failed to update date format:', error);
  } finally {
    busy.value = false;
    busyKey.value = null;
  }
};

// --- Timezone ---
const timezoneDisplay = computed(() => {
  const customer = sessionStore.getCustomerInfo();
  const jurisdiction = customer?.jurisdiction || 'SG';
  const offset = getTimezoneFromJurisdiction(jurisdiction);
  // Convert "+0800" -> "UTC+8", "+0530" -> "UTC+5:30"
  const sign = offset.charAt(0);
  const hours = parseInt(offset.substring(1, 3), 10);
  const minutes = parseInt(offset.substring(3, 5), 10);
  if (minutes === 0) {
    return `UTC${sign}${hours}`;
  }
  return `UTC${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
});
</script>

<template>
  <div class="header-info-bar d-flex align-center">
    <!-- Language: Flag + Code (clickable) -->
    <v-menu open-on-click location="bottom" :disabled="busy">
      <template v-slot:activator="{ props }">
        <div
          v-bind="props"
          class="info-item info-item--clickable d-flex align-center"
          :class="{ 'info-item--busy': busy }"
        >
          <img
            v-if="currentFlag"
            :key="currentFlag"
            :src="currentFlag"
            :alt="defaultLangCd"
            width="18"
            height="13"
            class="flag-img"
          />
          <span v-else class="info-text">{{ defaultLangCd.toUpperCase() }}</span>
          <v-progress-circular
            v-if="busyKey === 'lang'"
            indeterminate
            size="12"
            width="2"
            color="white"
            class="ms-1"
          />
          <v-icon v-else size="14" class="ms-1 chevron-icon">mdi-chevron-down</v-icon>
        </div>
      </template>
      <v-sheet rounded="md" width="320" elevation="10">
        <v-list class="theme-list" density="compact">
          <v-list-item
            v-for="(item, index) in supportedLanguages"
            :key="index"
            color="primary"
            :active="defaultLangCd == item.value"
            @click="changeLanguage(item.value)"
          >
            <template v-slot:prepend>
              <v-avatar rounded="md" size="28">
                <img
                  v-if="item.avatar"
                  :src="item.avatar"
                  :alt="item.value"
                  width="20"
                  height="14"
                  class="obj-cover"
                />
                <span v-else class="text-caption">{{ item.value.toUpperCase() }}</span>
              </v-avatar>
            </template>
            <v-list-item-title class="text-body-2">
              {{ item.title }}
              <span class="text-caption text-medium-emphasis ms-2">{{ item.subtext }}</span>
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-sheet>
    </v-menu>

    <span class="info-separator">|</span>

    <!-- Date Format (clickable) -->
    <v-menu open-on-click location="bottom" :disabled="busy">
      <template v-slot:activator="{ props }">
        <div
          v-bind="props"
          class="info-item info-item--clickable d-flex align-center"
          :class="{ 'info-item--busy': busy }"
        >
          <span class="info-text">{{ dateFormat }}</span>
          <v-progress-circular
            v-if="busyKey === 'date'"
            indeterminate
            size="12"
            width="2"
            color="white"
            class="ms-1"
          />
          <v-icon v-else size="14" class="ms-1 chevron-icon">mdi-chevron-down</v-icon>
        </div>
      </template>
      <v-sheet rounded="md" width="280" elevation="10">
        <v-list density="compact">
          <v-list-item
            v-for="opt in dateFormatOptions"
            :key="opt.value"
            :active="dateFormat === opt.value"
            color="primary"
            @click="changeDateFormat(opt.value)"
          >
            <v-list-item-title class="text-body-2">{{ opt.title }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-sheet>
    </v-menu>

    <span class="info-separator">|</span>

    <!-- Timezone (display only) -->
    <div class="info-item d-flex align-center">
      <v-icon size="14" class="me-1 tz-icon">mdi-clock-outline</v-icon>
      <span class="info-text">{{ timezoneDisplay }}</span>
    </div>
  </div>

  <v-snackbar v-model="langSyncFailedSnackbar" color="error" :timeout="4000" location="top">
    {{ t('common.languageSyncFailed') }}
  </v-snackbar>
</template>

<style scoped>
.header-info-bar {
  gap: 2px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  padding: 4px 10px;
  height: 32px;
}

.info-item {
  cursor: default;
  padding: 2px 4px;
  border-radius: 4px;
  white-space: nowrap;
}

.info-item--clickable {
  cursor: pointer;
  transition: background-color 0.2s;
}

.info-item--clickable:hover {
  background: rgba(255, 255, 255, 0.15);
}

/* 변경 저장 중: 셀렉터 잠금 표시(연속 클릭 방지) */
.info-item--busy {
  opacity: 0.7;
  cursor: progress;
  pointer-events: none;
}

.info-text {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
  letter-spacing: 0.3px;
}

.info-separator {
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
  margin: 0 2px;
  user-select: none;
}

.flag-img {
  border-radius: 2px;
  object-fit: cover;
}

.chevron-icon {
  color: rgba(255, 255, 255, 0.6);
}

.tz-icon {
  color: rgba(255, 255, 255, 0.7);
}
</style>
